import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Pagination,
  Stack,
  Chip as MuiChip,
} from '@mui/material';
import { Category, CheckCircle, Assignment } from '@mui/icons-material';
import { transactionsAPI, categoriesAPI } from '../services/api';

interface UnknownTransaction {
  id: number;
  amount: number;
  description: string;
  date: string;
  transaction_type: 'income' | 'expense' | 'transfer';
  category: { id: number; name: string; color: string };
  bank: { id: number; name: string };
  to_bank?: { id: number; name: string };
  to_bank_name?: string;
  reference_number?: string;
}

interface CategoryType {
  id: number;
  name: string;
  color: string;
}

interface PaginationInfo {
  current_page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
}

const UnknownTransactionsPage: React.FC = () => {
  const [transactions, setTransactions] = useState<UnknownTransaction[]>([]);
  const [categories, setCategories] = useState<CategoryType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [selectedTransactions, setSelectedTransactions] = useState<number[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [keywords, setKeywords] = useState('');
  const [updating, setUpdating] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchData();
  }, [currentPage]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [transactionsRes, categoriesRes] = await Promise.all([
        transactionsAPI.getUnknownTransactions({ page: currentPage }),
        categoriesAPI.getAll(),
      ]);
      
      if (transactionsRes.data.transactions) {
        setTransactions(transactionsRes.data.transactions);
        setPagination(transactionsRes.data.pagination);
      } else {
        setTransactions(transactionsRes.data);
        setPagination(null);
      }
      
      setCategories(categoriesRes.data);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedTransactions(transactions.map(t => t.id));
    } else {
      setSelectedTransactions([]);
    }
  };

  const handleSelectTransaction = (transactionId: number) => {
    setSelectedTransactions(prev => 
      prev.includes(transactionId)
        ? prev.filter(id => id !== transactionId)
        : [...prev, transactionId]
    );
  };

  const handleBulkAssign = () => {
    if (selectedTransactions.length === 0) {
      setError('Please select at least one transaction');
      return;
    }
    setDialogOpen(true);
  };

  const handleAssignCategory = async () => {
    if (!selectedCategory) {
      setError('Please select a category');
      return;
    }

    try {
      setUpdating(true);
      setError('');

      const keywordArray = keywords.split(',').map(k => k.trim()).filter(k => k.length > 0);
      
      await transactionsAPI.bulkUpdateCategory({
        transaction_ids: selectedTransactions,
        category_id: parseInt(selectedCategory),
        keywords: keywordArray,
      });

      setDialogOpen(false);
      setSelectedTransactions([]);
      setSelectedCategory('');
      setKeywords('');
      fetchData();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to update transactions');
    } finally {
      setUpdating(false);
    }
  };

  const formatCurrency = (value: number): string => {
    return `₹${value.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case 'income':
        return 'success';
      case 'expense':
        return 'error';
      case 'transfer':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Unknown Transactions</Typography>
        <Button
          variant="contained"
          startIcon={<Assignment />}
          onClick={handleBulkAssign}
          disabled={selectedTransactions.length === 0}
        >
          Assign Category ({selectedTransactions.length})
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {transactions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Category sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No Unknown Transactions
          </Typography>
          <Typography variant="body2" color="textSecondary">
            All transactions have been categorized. Great job!
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedTransactions.length > 0 && selectedTransactions.length < transactions.length}
                      checked={selectedTransactions.length === transactions.length && transactions.length > 0}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Bank</TableCell>
                  <TableCell>Reference</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedTransactions.includes(transaction.id)}
                        onChange={() => handleSelectTransaction(transaction.id)}
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(transaction.date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                        {transaction.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={transaction.transaction_type === 'income' ? 'success.main' : 'error.main'}
                      >
                        {transaction.transaction_type === 'income' ? '+' : '-'}
                        {formatCurrency(transaction.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={transaction.transaction_type}
                        color={getTransactionTypeColor(transaction.transaction_type) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{transaction.bank.name}</TableCell>
                    <TableCell>
                      {transaction.reference_number && (
                        <Typography variant="caption" color="textSecondary">
                          {transaction.reference_number}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {pagination && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Stack spacing={2} alignItems="center">
                <Typography variant="body2" color="textSecondary">
                  Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                  {Math.min(pagination.current_page * pagination.per_page, pagination.total_count)} of{' '}
                  {pagination.total_count} unknown transactions
                </Typography>
                <Pagination
                  count={pagination.total_pages}
                  page={pagination.current_page}
                  onChange={(event, page) => setCurrentPage(page)}
                  color="primary"
                />
              </Stack>
            </Box>
          )}
        </>
      )}

      {/* Assign Category Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Category to {selectedTransactions.length} Transaction(s)</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Select Category</InputLabel>
                <Select
                  value={selectedCategory}
                  label="Select Category"
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  {categories
                    .filter(category => category.name !== '❓ Unknown')
                    .map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        <Box display="flex" alignItems="center">
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              backgroundColor: category.color,
                              mr: 1,
                            }}
                          />
                          {category.name}
                        </Box>
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Keywords for Learning (comma-separated)"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="e.g., restaurant, food, dining"
                helperText="These keywords will be used to automatically categorize similar transactions in the future"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAssignCategory}
            variant="contained"
            disabled={!selectedCategory || updating}
            startIcon={updating ? <CircularProgress size={20} /> : <CheckCircle />}
          >
            {updating ? 'Assigning...' : 'Assign Category'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UnknownTransactionsPage;
